import { IInvitation, ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { IUserSetting, IUserSpeedData, IUserState, ESETTINGS_KEYS } from "./user/interfaces";
import { IDetailState } from "./detail/interfaces";
import { IMapState } from "./map/interfaces";
import { IRibbonState } from "./ribbon/interfaces";
import { IMyMapsState } from "./mymaps/interfaces";

function defaultCompare<T>(first: T, second: T): boolean {
	return first === second;
}

function getValue<T>(object: object = {}, path = ""): T {
	const [first, ...rest] = path.split(".");

	if (rest.length) {
		return object[first] ? getValue(object[first], rest.join(".")) : undefined;
	}

	return object[first];
}

export function watch<T>(getState, objectPath: string, compare: (first: T, second: T) => boolean = defaultCompare) {
	let currentValue: T = getValue(getState(), objectPath);

	return function wt(fn: (newValue: T, oldValue: T, objectPath: string) => void) {
		return function newWt() {
			const newValue = getValue<T>(getState(), objectPath);

			if (!compare(currentValue, newValue)) {
				const oldValue = currentValue;

				currentValue = newValue;
				fn(newValue, oldValue, objectPath);
			}
		};
	};
}

export function watchUser(first: IUserState, second: IUserState): boolean {
	return first.user.rusId === second.user.rusId && first.accounts.length === second.accounts.length && first.user.state === second.user.state;
}

export function watchCurrentUser(first: IUserState, second: IUserState): boolean {
	return first.user.rusId === second.user.rusId && first.user.state === second.user.state;
}

export function watchPremiumData(first: IUserState, second: IUserState): boolean {
	return JSON.stringify(first.user.premiumData) === JSON.stringify(second.user.premiumData) && first.user.premium === second.user.premium && first.user.sbr === second.user.sbr;
}

export function watchSubscriptions(first: Array<ISubscriptionWithDescription>, second: Array<ISubscriptionWithDescription>): boolean {
	return JSON.stringify(first) === JSON.stringify(second);
}

export function watchInvitations(first: Array<IInvitation>, second: Array<IInvitation>): boolean {
	return JSON.stringify(first) === JSON.stringify(second);
}

export function watchNotifications(first: IUserState, second: IUserState): boolean {
	return first.notifications.total === second.notifications.total && first.notifications.totalNew === second.notifications.totalNew;
}

export function watchDetail(first: IDetailState, second: IDetailState): boolean {
	// TODO: detekovat nejak i zmenu v data
	return first.source === second.source && first.id === second.id && first.loading === second.loading && first.status === second.status;
}

export function watchMapset(first: IMapState, second: IMapState): boolean {
	return first.mapset === second.mapset && first.provider === second.provider && JSON.stringify(first.overlays) === JSON.stringify(second.overlays);
}

export function watchMapsetAndDesaturate(first: IMapState, second: IMapState): boolean {
	return first.mapset === second.mapset && first.provider === second.provider && JSON.stringify(first.overlays) === JSON.stringify(second.overlays) && first.desaturate === second.desaturate;
}

export function watchPadding(first: IMapState, second: IMapState): boolean {
	return JSON.stringify(first.padding) === JSON.stringify(second.padding);
}

export function watchMapProvider(first: IMapState, second: IMapState): boolean {
	return first.provider === second.provider;
}

export function watchMap(first: IMapState, second: IMapState): boolean {
	return (
		first.mapset === second.mapset
		&& first.provider === second.provider
		&& JSON.stringify(first.overlays) === JSON.stringify(second.overlays)
		&& first.longitude === second.longitude
		&& first.latitude === second.latitude
		&& first.zoom === second.zoom
		&& first.yaw === second.yaw
		&& first.pitch === second.pitch
		&& first.height === second.height
	);
}

export function watchMapEggs(first: IMapState, second: IMapState): boolean {
	return (
		first.egg === second.egg
	);
}

export function watchCoordinates(first: IMapState, second: IMapState): boolean {
	return (
		first.longitude === second.longitude
		&& first.latitude === second.latitude
		&& first.zoom === second.zoom
		&& first.yaw === second.yaw
		&& first.pitch === second.pitch
		&& first.height === second.height
		&& first.animating === second.animating
	);
}

export function watchMapPosition(first: IMapState, second: IMapState): boolean {
	return (
		first.longitude === second.longitude
		&& first.latitude === second.latitude
		&& first.zoom === second.zoom
	);
}

export function watchMapZoom(first: IMapState, second: IMapState): boolean {
	const zoomA = first.zoom >>> 0;
	const zoomB = second.zoom >>> 0;

	return zoomA === zoomB;
}

export function watchAnimation(first: IMapState, second: IMapState): boolean {
	return first.animating === second.animating;
}

export function watchMapOverlays(first: IMapState, second: IMapState): boolean {
	return (
		JSON.stringify(first.overlays) === JSON.stringify(second.overlays)
	);
}

export function watchOphotoDate(first: IMapState, second: IMapState): boolean {
	return first.ophotoDate === second.ophotoDate;
}

export function watchOphotoTimeline(first: IMapState, second: IMapState): boolean {
	return first.ophotoTimeline === second.ophotoTimeline;
}

export function watchFit(first: IMapState, second: IMapState): boolean {
	return first.fit.length === second.fit.length && JSON.stringify(first.fit) === JSON.stringify(second.fit);
}

export function watchCopyrights(first: IMapState, second: IMapState): boolean {
	return (
		first.ophotoDate === second.ophotoDate
		&& first.copyrights.join("") === second.copyrights.join("")
		&& first.longitude === second.longitude
		&& first.latitude === second.latitude
	);
}

export function watchRibbonContent(first: IRibbonState, second: IRibbonState): boolean {
	return (
		first.activeContents.join("") === second.activeContents.join("")
	);
}

export function watchRibbonEnabled(first: IRibbonState, second: IRibbonState): boolean {
	return (
		first.enabled === second.enabled
	);
}

export function watchSettingsUserSpeedChange(first: Array<IUserSetting>, second: Array<IUserSetting>): boolean {
	const firstUserSpeed = (first.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value) as IUserSpeedData;
	const secondUserSpeed = (second.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value) as IUserSpeedData;

	return JSON.stringify(firstUserSpeed) === JSON.stringify(secondUserSpeed);
}

export function watchAlwaysOnMapItemsCount(first: IMyMapsState, second: IMyMapsState): boolean {
	return first.alwaysOnMapItemsCount === second.alwaysOnMapItemsCount && first.alwaysOnMapFoldersCount === second.alwaysOnMapFoldersCount;
}
